import { Box } from '@mui/material';
import classNames from 'classnames';
import React, { memo } from 'react';
import { useIntl } from 'react-intl';

import { ensureCurrentUser, ensureUser, userAbbreviatedName, userDisplayNameAsString } from '../../util/data';
import IconBannedUser from '../Icons/IconBannedUser/IconBannedUser';
import ResponsiveImage from '../Image/ResponsiveImage/ResponsiveImage';
import NamedLink from '../Links/NamedLink/NamedLink';

// Responsive image sizes hint
const AVATAR_SIZES = '40px';
const AVATAR_SIZES_MEDIUM = '60px';
const AVATAR_SIZES_LARGE = '96px';

const AVATAR_IMAGE_VARIANTS = [
  // 40x40
  'square-xsmall',

  // 80x80
  'square-xsmall2x',

  // 240x240
  'square-small',

  // 480x480
  'square-small2x',
];

// .avatarBase shared declarations (composed by .root / .mediumAvatar / .largeAvatar).
// The initials font-size/weight rules are descendant selectors in the original CSS
// (.root .initials, .mediumAvatar .initials, .largeAvatar .initials), reproduced here
// via the `& .avatarInitials` nested selector.
const avatarBaseSx = (theme) => ({
  position: 'relative',
  fontFamily: "'sofiapro', Helvetica, Arial, sans-serif",
  borderRadius: '50%',
  // Position possible initials to the center of the component
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Colors
  backgroundImage: `linear-gradient(-180deg, ${theme.tokens.marketplaceColorLight} 0%, ${theme.tokens.marketplaceColor} 100%)`,
  color: theme.tokens.matterColorLight,
  '&:hover': {
    textDecoration: 'none',
  },
});

// .root (small avatar): avatarBase + 40x40, default initials 14px/bold
const rootSx = (theme) => ({
  ...avatarBaseSx(theme),
  width: '40px',
  height: '40px',
  '& .avatarInitials': {
    fontSize: '14px',
    fontWeight: theme.tokens.fontWeightBold,
  },
});

// .mediumAvatar: avatarBase + 60x60, initials 20px/semiBold
const mediumAvatarSx = (theme) => ({
  ...avatarBaseSx(theme),
  width: '60px',
  height: '60px',
  '& .avatarInitials': {
    fontSize: '20px',
    fontWeight: theme.tokens.fontWeightSemiBold,
  },
});

// .largeAvatar: avatarBase + 96x96, initials 30px/semiBold
const largeAvatarSx = (theme) => ({
  ...avatarBaseSx(theme),
  width: '96px',
  height: '96px',
  '& .avatarInitials': {
    fontSize: '30px',
    fontWeight: theme.tokens.fontWeightSemiBold,
  },
});

// .avatarImage
const avatarImageSx = { width: '100%', height: '100%', borderRadius: 'inherit' };

// .bannedUserIcon
const bannedUserIconSx = { width: '100%', height: '100%' };

export const AvatarComponent = ({
  className = null,
  rootClassName = null,
  rootSxOverride = null,
  sx = null,
  user = null,
  renderSizes = AVATAR_SIZES,
  disableProfileLink = false,
}) => {
  const intl = useIntl();
  // Preserve the Sharetribe className API: the component's own base style applies ONLY when no
  // rootClassName override is passed; className is always appended. AvatarMedium/AvatarLarge pass
  // their size variant as rootSxOverride (replacing the base root style), just like the original
  // rootClassName={css.mediumAvatar/largeAvatar} did.
  // Optional additive `sx` is appended on top of the base style (default null → no change for
  // existing callers). Used by callers that previously passed extra styling via `className`.
  const resolvedBaseSx = rootClassName ? rootSxOverride : rootSx;
  const baseSx = sx ? [resolvedBaseSx, sx] : resolvedBaseSx;
  const classes = classNames(className) || undefined;

  const userIsCurrentUser = user && user.type === 'currentUser';
  const avatarUser = userIsCurrentUser ? ensureCurrentUser(user) : ensureUser(user);

  const isBannedUser = avatarUser.attributes.banned;
  const isDeletedUser = avatarUser.attributes.deleted;

  const bannedUserDisplayName = intl.formatMessage({
    id: 'Avatar.bannedUserDisplayName',
  });

  const deletedUserDisplayName = intl.formatMessage({
    id: 'Avatar.deletedUserDisplayName',
  });

  const defaultUserDisplayName = isBannedUser
    ? bannedUserDisplayName
    : isDeletedUser
    ? deletedUserDisplayName
    : '';

  const defaultUserAbbreviatedName = '';

  const displayName = userDisplayNameAsString(avatarUser, defaultUserDisplayName);
  const abbreviatedName = userAbbreviatedName(avatarUser, defaultUserAbbreviatedName);
  const linkProps = avatarUser.id
    ? { name: 'ProfilePage', params: { id: avatarUser.id.uuid } }
    : { name: 'ProfileBasePage' };
  const hasProfileImage = avatarUser.profileImage && avatarUser.profileImage.id;
  const profileLinkEnabled = !disableProfileLink;

  if (isBannedUser || isDeletedUser) {
    return (
      <Box className={classes} sx={baseSx} title={displayName}>
        <IconBannedUser sx={bannedUserIconSx} />
      </Box>
    );
  } else if (hasProfileImage && profileLinkEnabled) {
    return (
      <Box className={classes} component={NamedLink} sx={baseSx} title={displayName} {...linkProps}>
        <ResponsiveImage
          alt={displayName}
          image={avatarUser.profileImage}
          sizes={renderSizes}
          sx={avatarImageSx}
          variants={AVATAR_IMAGE_VARIANTS}
        />
      </Box>
    );
  } else if (hasProfileImage) {
    return (
      <Box className={classes} sx={baseSx} title={displayName}>
        <ResponsiveImage
          alt={displayName}
          image={avatarUser.profileImage}
          sizes={renderSizes}
          sx={avatarImageSx}
          variants={AVATAR_IMAGE_VARIANTS}
        />
      </Box>
    );
  } else if (profileLinkEnabled) {
    // Placeholder avatar (initials)
    return (
      <Box className={classes} component={NamedLink} sx={baseSx} title={displayName} {...linkProps}>
        <Box className="avatarInitials" component="span">
          {abbreviatedName}
        </Box>
      </Box>
    );
  } else {
    // Placeholder avatar (initials)
    return (
      <Box className={classes} sx={baseSx} title={displayName}>
        <Box className="avatarInitials" component="span">
          {abbreviatedName}
        </Box>
      </Box>
    );
  }
};

AvatarComponent.displayName = 'Avatar';

const Avatar = memo(AvatarComponent);

export default Avatar;

export const AvatarMedium = (props) => (
  <Avatar renderSizes={AVATAR_SIZES_MEDIUM} rootClassName="mediumAvatar" rootSxOverride={mediumAvatarSx} {...props} />
);
AvatarMedium.displayName = 'AvatarMedium';

export const AvatarLarge = (props) => (
  <Avatar renderSizes={AVATAR_SIZES_LARGE} rootClassName="largeAvatar" rootSxOverride={largeAvatarSx} {...props} />
);
AvatarLarge.displayName = 'AvatarLarge';
